import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiCheck, mdiContentCopy } from '@mdi/js'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { AppIcon } from '../ui/icon'
import {
    beginTotpEnrollment,
    confirmEnrollment,
    MfaApiError,
    mfaErrorKey,
    type MFAConfirmResult,
    type MFAEnrollment,
} from '../../lib/mfa'

/**
 * Enrolls an authenticator app (spec 2026-10-01-03): the QR code and its
 * secret, then the first code. Used by the forced-enrollment page and the
 * Security tab; `onConfirmed` gets the factor and the upgraded access token of
 * the session.
 */
export function TotpEnrollment({
    org,
    onConfirmed,
}: {
    org: string
    onConfirmed: (result: MFAConfirmResult) => void
}) {
    const { t } = useTranslation('auth')
    const [enrollment, setEnrollment] = useState<MFAEnrollment | null>(null)
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)

    const start = async () => {
        setBusy(true)
        setError(null)
        try {
            setEnrollment(await beginTotpEnrollment(org))
        } catch (err) {
            setError(t(mfaErrorKey(err instanceof MfaApiError ? err.code : null)))
        } finally {
            setBusy(false)
        }
    }

    const confirm = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!enrollment || !code.trim()) return
        setBusy(true)
        setError(null)
        try {
            onConfirmed(await confirmEnrollment(org, 'totp', enrollment.enrollment_token, code.trim()))
        } catch (err) {
            setError(t(mfaErrorKey(err instanceof MfaApiError ? err.code : null)))
            setCode('')
        } finally {
            setBusy(false)
        }
    }

    const copySecret = async () => {
        if (!enrollment?.totp) return
        try {
            await navigator.clipboard.writeText(enrollment.totp.secret)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Clipboard refused (permissions): the key stays selectable.
        }
    }

    if (!enrollment?.totp) {
        return (
            <div className="space-y-4" data-testid="totp-enroll-start">
                <p className="text-sm text-muted-foreground">{t('mfa.setup.intro')}</p>
                {error && <p className="text-sm text-destructive" data-testid="totp-error">{error}</p>}
                <Button onClick={start} disabled={busy} className="w-full" data-testid="totp-start-btn">
                    {busy ? t('mfa.setup.starting') : t('mfa.setup.start')}
                </Button>
            </div>
        )
    }

    const totp = enrollment.totp
    // Split in groups of four so the key reads and types easily.
    const groupedSecret = totp.secret.replace(/(.{4})/g, '$1 ').trim()

    return (
        <form onSubmit={confirm} className="space-y-4" data-testid="totp-enroll-confirm">
            <p className="text-sm text-muted-foreground">{t('mfa.setup.scan')}</p>
            {/* QR codes need a light quiet zone to scan, whatever the theme. */}
            <div className="flex justify-center">
                <div className="rounded-lg bg-white p-3 border border-border">
                    <img src={totp.qr_code} alt="" width={176} height={176} className="h-44 w-44" data-testid="totp-qr" />
                </div>
            </div>
            <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('mfa.setup.manual')}</p>
                <div className="flex items-center gap-2">
                    <code
                        className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all select-all"
                        data-testid="totp-secret"
                        data-secret={totp.secret}
                    >
                        {groupedSecret}
                    </code>
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={copySecret}
                        aria-label={copied ? t('mfa.setup.copied') : t('mfa.setup.copy')}
                        title={copied ? t('mfa.setup.copied') : t('mfa.setup.copy')}
                        data-testid="totp-copy-secret"
                    >
                        <AppIcon path={copied ? mdiCheck : mdiContentCopy} size="sm" />
                    </Button>
                </div>
            </div>
            <div className="space-y-2">
                <Label htmlFor="totp-code">{t('mfa.setup.code_label')}</Label>
                <Input
                    id="totp-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={7}
                    className="font-mono tracking-widest"
                    autoFocus
                    data-testid="totp-code-input"
                />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="totp-error">{error}</p>}
            <Button type="submit" disabled={busy || !code.trim()} className="w-full" data-testid="totp-confirm-btn">
                {busy ? t('mfa.setup.confirming') : t('mfa.setup.confirm')}
            </Button>
        </form>
    )
}
